import { useEffect, useRef } from "react";
import { useWorldT } from "../i18n.js";
import { TIME_PRESETS, formatHours, hoursForSetting, presetOf } from "../look/time.js";
import type { TimePreset } from "../look/time.js";
import { previewTimeSetting, setTimeSetting, useTimeSetting } from "../look/timeStore.js";
import { Segmented, SettingRow } from "./SettingsParts.js";

const PRESETS: readonly TimePreset[] = ["day", "dusk", "night"];

/**
 * The time-of-day control, a block of the settings sheet: Auto (clock), Day, Dusk, Night and an
 * hour slider. The root keeps the data attributes the tests and the assistant hook read.
 */
export function TimeControl() {
  const t = useWorldT();
  const setting = useTimeSetting();
  const hours = hoursForSetting(setting);
  const preset = presetOf(setting);
  const commit = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(commit.current), []);
  const mode = setting === "auto" ? "auto" : (preset ?? "custom");
  return (
    <div
      className="hud-time"
      data-testid="world-time"
      data-setting={String(setting)}
      data-hours={hours.toFixed(2)}
      data-mode={mode}
    >
      <SettingRow label={t("time.label")} hint={t("game.timeHint")} stacked>
        <Segmented
          label={t("time.label")}
          options={[
            { id: "auto", label: t("time.auto"), testId: "time-auto", pressed: setting === "auto" },
            ...PRESETS.map((p) => ({
              id: p,
              label: t(`time.${p}`),
              testId: `time-${p}`,
              pressed: preset === p,
            })),
          ]}
          onPick={(id) => setTimeSetting(id === "auto" ? "auto" : TIME_PRESETS[id as TimePreset])}
        />
      </SettingRow>
      <SettingRow label={t("time.hour")} value={formatHours(hours)} stacked>
        <input
          type="range"
          className="hud-slider"
          min={0}
          max={24}
          step={0.25}
          value={hours}
          data-testid="time-slider"
          aria-label={t("time.hour")}
          aria-valuetext={formatHours(hours)}
          onChange={(e) => {
            const v = Number(e.target.value);
            previewTimeSetting(v);
            clearTimeout(commit.current);
            commit.current = setTimeout(() => setTimeSetting(v), 350);
          }}
        />
      </SettingRow>
    </div>
  );
}
